import React, { useState } from "react";
import { plateStockType } from "@apv-erp/status";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  Link as LinkIcon,
  Download,
  PowerOff,
  ShieldAlert,
  CheckCircle2,
  Printer,
  Workflow,
  MessageSquare,
  Paperclip,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PrintDialog from "@/components/shared/PrintDialog";
import PlatePrintTemplate from "@/components/tech/print/PlatePrintTemplate";
import ProofApprovalModal from "./ProofApprovalModal";
import MountPlateToMachineModal from "./MountPlateToMachineModal";
import PlateDamageRemakeModal from "./PlateDamageRemakeModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { ZIndexView, useZIndexView } from "@/components/shared/ZIndexView";
import EntityLink from "@/components/shared/EntityLink";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import CommentSection from "@/components/shared/CommentSection";
import AttachmentSection from "@/components/shared/AttachmentSection";
import ActivityLogSection from "@/components/shared/ActivityLogSection";
import { getStatusConfig } from "@/config/status-registry";
import { formatNumber, formatDate, cn, renderDetailValue } from "@/lib/utils";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import { DetailField as F } from "@/components/shared/DetailField";


export default function PlateDetailPage({
  plateId: propId,
  isZIndex,
}: {
  plateId?: string;
  isZIndex?: boolean;
} = {}) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as any;
  const plateId = propId || params.plateId;
  const queryClient = useQueryClient();
  const [showDelete, setShowDelete] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [printType, setPrintType] = useState<"ctp_plate" | "plate_handover">("ctp_plate");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showProofApproval, setShowProofApproval] = useState(false);
  const [showMountMachine, setShowMountMachine] = useState(false);
  const [showDamageRemake, setShowDamageRemake] = useState(false);
  const zIndex = useZIndexView();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await (trpcClient as any).technical.plates.delete.mutate({ id: plateId });

      await queryClient.invalidateQueries({ queryKey: ["plates"] });
      navigate({ to: "/plates" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  const {
    data: pl,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["plate", plateId],
    queryFn: async () =>
      (trpcClient as any).technical.plates.getById.query({ id: plateId }),
  });

  const d = pl as any;

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      return (trpcClient as any).technical.plates.updateStatus.mutate({
        id: plateId,
        status: newStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plate", plateId] });
      queryClient.invalidateQueries({ queryKey: ["plates"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Có lỗi xảy ra khi cập nhật trạng thái");
    },
  });

  const handleUpdateStatus = (newStatus: string) => {
    updateStatusMutation.mutate(newStatus);
  };

  const { data: usageHistory = [] } = useQuery({
    queryKey: ["plate-usage-history", d?.id || plateId],
    queryFn: async () => {
      return (trpcClient as any).technical.plates.getUsageHistory.query({
        id: d?.id || plateId,
      });
    },
    enabled: !!(d?.id || plateId),
  });


  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Chế bản", to: "/plates" }]}
        entityLabel="Bản kẽm"
        backTo="/plates"
        codeOrId={plateId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Chế bản", to: "/plates" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  const sts = getStatusConfig("ToolingStatus", d.status);
  const currentUsage = d.usageCount || 0;

  return (
    <>
      <StandardDetailLayout
        isZIndex={isZIndex}
        breadcrumbs={[{ label: "Chế bản", to: "/plates" }]}
        title={d.plateId || plateId}
        entityId={d.id}
        entityType="plate"
        showContextPanel={false}
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={
          <Badge variant="outline" className={sts.color}>
            {sts.label}
          </Badge>
        }
        actions={
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="print:hidden"
              onClick={() => {
                setPrintType("ctp_plate");
                setShowPrint(true);
              }}
            >
              <Printer size={14} className="mr-1.5" /> In lệnh ghi
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="print:hidden text-indigo-700 border-indigo-200 hover:bg-indigo-50"
              onClick={() => {
                setPrintType("plate_handover");
                setShowPrint(true);
              }}
            >
              <Printer size={14} className="mr-1.5" /> In bàn giao
            </Button>

            {/* Chốt chặn Duyệt Mẫu Proof */}
            <Button
              variant="outline"
              size="sm"
              className={cn(
                d.proofStatus === "APPROVED"
                  ? "text-emerald-700 border-emerald-300 bg-emerald-50/50 hover:bg-emerald-100/50"
                  : "text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
              )}
              onClick={() => setShowProofApproval(true)}
            >
              <CheckCircle2 size={14} className="mr-1.5" />
              {d.proofStatus === "APPROVED" ? "Proof đã duyệt" : "Duyệt proof"}
            </Button>

            {d.status === "AVAILABLE" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => setShowMountMachine(true)}
                >
                  <Printer size={14} className="mr-1.5" /> Lắp máy in
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => setShowDamageRemake(true)}
                >
                  <ShieldAlert size={14} className="mr-1.5" /> Báo hỏng kẽm
                </Button>
              </>
            )}
            {d.status === "IN_USE" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-slate-700 border-slate-300 hover:bg-slate-100"
                  onClick={() => handleUpdateStatus("DISCARDED")}
                  disabled={updateStatusMutation.isPending}
                >
                  <PowerOff size={14} className="mr-1.5" /> Thanh lý
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => setShowDamageRemake(true)}
                >
                  <ShieldAlert size={14} className="mr-1.5" /> Báo hỏng
                </Button>
              </>
            )}
            {d.status === "DAMAGED" && (
              <Button
                variant="outline"
                size="sm"
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => setShowDamageRemake(true)}
              >
                <ShieldAlert size={14} className="mr-1.5" /> Ghi bù kẽm ({d.remakeCount || 0})
              </Button>
            )}
            {d.status === "PENDING" && (
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => handleUpdateStatus("AVAILABLE")}
                disabled={updateStatusMutation.isPending}
              >
                <CheckCircle2 size={14} className="mr-1.5" /> Nghiệm thu ghi kẽm
              </Button>
            )}
            {d.status !== "DISCARDED" && d.status !== "IN_USE" && (
              <Button
                variant="outline"
                size="sm"
                className="text-slate-600 border-slate-200 hover:bg-slate-50"
                onClick={() => handleUpdateStatus("DISCARDED")}
                disabled={updateStatusMutation.isPending}
              >
                <PowerOff size={14} className="mr-1.5" /> Thanh lý
              </Button>
            )}

            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/plates/$plateId/edit" as any,
                  params: { plateId } as any,
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
      >
        <div className="border rounded-lg bg-white shadow-sm p-4">
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
              <F label="Mã bài in" value={d.plateId} mono hl />
              <F label="Tên bài in" value={d.name} />
              <F
                label="Sản phẩm"
                value={
                  <EntityLink
                    entityType="product"
                    id={d.productId}
                    label={d.product?.productName || d.productName}
                    search={{ returnUrl: `/plates/${d.id || plateId}` }}
                  />
                }
              />
              <F
                label="Khách hàng"
                value={
                  <EntityLink
                    entityType="customer"
                    id={d.customerId || d.product?.customer?.id}
                    label={
                      d.customer?.shortName ||
                      d.customer?.name ||
                      d.product?.customer?.shortName ||
                      d.product?.customer?.name
                    }
                    onClick={isZIndex && (d.customerId || d.product?.customer?.id) ? (e) => { e.preventDefault(); zIndex.openView("customer", d.customerId || d.product?.customer?.id); } : undefined}
                  />
                }
              />
            </div>
            <div className="border-t border-slate-100" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
              <F label="Kích thước" value={d.size} mono />
              <F
                label="Màu in"
                size="medium"
                value={
                  (d.colors || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1 min-w-0">
                      {d.colors.map((c: string) => (
                        <Badge key={c} variant="outline" className="text-[10px] h-5 px-2">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  ) : null
                }
              />
              <div className="flex flex-col gap-2 min-w-0">
                <span className="text-slate-500 text-xs shrink-0 flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" /> File Kỹ thuật
                </span>
                <div className="flex flex-wrap gap-2">
                  {d.designFileUrl ? (
                    <a
                      href={d.designFileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs flex items-center gap-1 text-indigo-600 hover:underline bg-indigo-50 px-2 py-1 rounded"
                    >
                      <Download className="w-3 h-3" /> File Thiết kế
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">
                      Không có File thiết kế
                    </span>
                  )}
                  {d.ctpFileUrl ? (
                    <a
                      href={d.ctpFileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs flex items-center gap-1 text-rose-600 hover:underline bg-rose-50 px-2 py-1 rounded"
                    >
                      <Download className="w-3 h-3" /> File CTP (PDF)
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">
                      Không có File CTP
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100" />

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
              {/* Máy in chỉ định / Công nghệ kẽm bỏ (02/11/2026): bản kẽm không lưu 2 trường này, trước đây hiện chữ mẫu cố định */}
              <F label="Khu vực CTP" value={d.location} mono hl />
            </div>

            {/* Hàng thông tin Proofing & Cấp phát máy in (Phase 222) */}
            <div className="border-t border-slate-100" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 bg-slate-50/70 p-3 rounded-lg border border-slate-200">
              <F
                label="Duyệt Mẫu Proof"
                value={
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge
                      variant="outline"
                      className={
                        d.proofStatus === "APPROVED"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]"
                          : d.proofStatus === "REJECTED"
                          ? "bg-rose-50 text-rose-700 border-rose-300 text-[10px]"
                          : "bg-amber-50 text-amber-700 border-amber-300 text-[10px]"
                      }
                    >
                      {d.proofStatus === "APPROVED"
                        ? "✓ Đã Duyệt Mẫu Màu"
                        : d.proofStatus === "REJECTED"
                        ? "✗ Bị Từ Chối"
                        : "⏳ Chờ Duyệt Proof"}
                    </Badge>
                    {d.proofApprover && (
                      <span className="text-xs text-slate-600">({d.proofApprover})</span>
                    )}
                  </div>
                }
              />
              <F
                label="Máy in Offset"
                value={
                  d.activeMachineId ? (
                    <EntityLink entityType="machine" id={d.activeMachineId} label={d.activeMachineName} />
                  ) : d.activeMachineName ? (
                    <span className="font-semibold text-blue-700 flex items-center gap-1">
                      <Printer className="w-3.5 h-3.5 text-blue-500" />
                      {d.activeMachineName}
                    </span>
                  ) : (
                    <span className="text-slate-400">Chưa cấp phát máy in</span>
                  )
                }
              />
              <F
                label="Thợ máy nhận kẽm"
                value={
                  d.mountedBy ? (
                    <span className="font-medium text-slate-800">{d.mountedBy}</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )
                }
              />
              {d.proofNotes && (
                <div className="col-span-1 sm:col-span-2 md:col-span-3 text-xs text-slate-700 bg-white p-2 rounded border border-slate-200">
                  <span className="font-semibold text-slate-800">Thông số kỹ thuật Proof:</span> {d.proofNotes}
                </div>
              )}
              {d.remakeCount > 0 && (
                <div className="col-span-1 sm:col-span-2 md:col-span-3 text-xs text-rose-800 bg-rose-50 p-2 rounded border border-rose-200 flex items-center justify-between flex-wrap gap-2">
                  <span>
                    ⚠️ <strong>Đã ghi bù {d.remakeCount} lần:</strong> {d.damageReason || "Sự cố kẽm máy in"}
                  </span>
                  {d.damagedColors && d.damagedColors.length > 0 && (
                    <Badge variant="outline" className="border-rose-300 bg-white text-rose-700 text-[10px]">
                      Màu hỏng: {d.damagedColors.join(", ")}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Chi tiết từng lá kẽm thành phần — chỉ hiện màu đã lưu; góc tram / LPI và bộ CMYK mặc định đã bỏ (02/11/2026): không phải dữ liệu bản kẽm */}
            {(d.colors || []).length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <div className="text-xs font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                  <Printer className="w-3.5 h-3.5 text-indigo-600" />
                  Lá kẽm thành phần
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {d.colors.map((c: string, idx: number) => (
                    <div key={idx} className="p-2.5 bg-slate-50/80 rounded-lg border border-slate-200 text-xs">
                      <div className="font-bold text-slate-900">{c}</div>
                      <div className="text-[10px] text-emerald-700 font-medium mt-1">Khổ {plateStockType(d.size)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bảng giải thích nguyên tắc kẽm CTP + thông số cố định (LPI/DPI, khổ mặc định) đã bỏ (30/10/2026):
            không phải dữ liệu của bản kẽm, đẩy lịch sử ca in xuống dưới màn hình */}
        {/* ═══ TABS: LỊCH SỬ CA IN OFFSET & CHI TIẾT ═══ */}
        <RelatedDataTabs
          tabs={[
            {
              id: "shift-history",
              label: "Lịch sử in Offset & Ca máy",
              count: (usageHistory as any[]).length,
              icon: <Workflow className="w-4 h-4 text-indigo-500" />,
              content: (
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h4 className="text-sm font-bold text-slate-900">
                      Nhật ký ca in sử dụng bản kẽm ({(usageHistory as any[]).length})
                    </h4>
                    <span className="text-xs text-slate-500 font-mono">
                      Tổng lượt in: <strong className="text-indigo-600">{formatNumber(currentUsage)}</strong> lượt
                    </span>
                  </div>
                  {(usageHistory as any[]).length === 0 ? (
                    <div className="p-8 text-center text-slate-400 border border-dashed rounded-lg bg-slate-50/50 text-xs">
                      Chưa có ca in offset nào ghi nhận lượt in cho bản kẽm này
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-x-auto bg-white">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold">Lệnh SX / PO</th>
                            <th className="px-3 py-2 text-left font-semibold">Máy in Offset</th>
                            <th className="px-3 py-2 text-left font-semibold">Thợ in</th>
                            <th className="px-3 py-2 text-right font-semibold">Số lượt in</th>
                            <th className="px-3 py-2 text-left font-semibold">Thời gian</th>
                            <th className="px-3 py-2 text-left font-semibold">Ghi chú</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(usageHistory as any[]).map((uh: any) => (
                            <tr key={uh.id} className="hover:bg-slate-50/60">
                              <td className="px-3 py-2 font-mono font-bold text-indigo-600">
                                {uh.poCode || uh.productionOrderId ? (
                                  <EntityLink
                                    entityType="productionOrder"
                                    id={uh.productionOrderId || uh.poCode}
                                    code={uh.poCode || uh.productionOrderId}
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-700 font-medium">
                                {uh.machineName || uh.machineId || "—"}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {uh.operatorName || uh.operatorId || "—"}
                              </td>
                              <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                                {formatNumber(uh.impressions || 0)}
                              </td>
                              <td className="px-3 py-2 text-slate-600">
                                {formatDate(uh.startedAt)}
                              </td>
                              <td className="px-3 py-2 text-slate-500">
                                {uh.notes || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ),
            },
            {
              id: "comments",
              label: "Thảo luận & Ghi chú",
              icon: <MessageSquare className="w-4 h-4 text-blue-500" />,
              content: (
                <div className="p-4">
                  <CommentSection entityType="plate" entityId={d?.id || plateId} />
                </div>
              ),
            },
            {
              id: "attachments",
              label: "Tệp đính kèm",
              icon: <Paperclip className="w-4 h-4 text-emerald-500" />,
              content: (
                <div className="p-4">
                  <AttachmentSection entityType="plate" entityId={d?.id || plateId} />
                </div>
              ),
            },
            {
              id: "history",
              label: "Lịch sử cập nhật",
              icon: <History className="w-4 h-4 text-slate-500" />,
              content: (
                <div className="p-4">
                  <ActivityLogSection entityType="plate" entityId={d?.id || plateId} />
                </div>
              ),
            },
          ]}
          defaultTab="shift-history"
        />
      </StandardDetailLayout>

      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type={printType}
        data={d}
      />

      <ProofApprovalModal
        open={showProofApproval}
        onOpenChange={setShowProofApproval}
        plate={d}
      />

      <MountPlateToMachineModal
        open={showMountMachine}
        onOpenChange={setShowMountMachine}
        plate={d}
      />

      <PlateDamageRemakeModal
        open={showDamageRemake}
        onOpenChange={setShowDamageRemake}
        plate={d}
      />

      <ZIndexView {...zIndex} />

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xoá bài in {d.plateId}?</AlertDialogTitle>
            <AlertDialogDescription>
              Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {isDeleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Xoá
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
